import { useEffect } from "react";
import { AlertTriangle, CheckCircle2, Download, FolderOpen, Loader2, RotateCw, X } from "@fg/icons";
import { useAccessToken } from "@/auth/useAccessToken";
import { formatBytes } from "@/lib/drive";
import { cn } from "@/lib/utils";
import { recordingSaver, useRecordingSaveJobs, type RecordingSaveJob } from "./recordingSaver";

/**
 * Small, non-blocking progress cards for meeting recordings being saved to
 * Drive. Mounted once at the app root, so a save keeps showing (and keeps
 * going) after the host leaves the meeting.
 */
export function RecordingSaveToasts() {
    const token = useAccessToken();
    const jobs = useRecordingSaveJobs();
    useEffect(() => { recordingSaver.setToken(token); }, [token]);
    if (jobs.length === 0) return null;
    return (
        <div className="pointer-events-none fixed bottom-4 left-4 z-[60] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2" aria-live="polite">
            {jobs.map((j) => <JobCard key={j.id} job={j} />)}
        </div>
    );
}

function title(job: RecordingSaveJob): string {
    switch (job.phase) {
        case "uploading": return `Saving recording to your Drive… ${Math.round(job.progress * 100)}%`;
        case "sharing": return "Sharing with attendees…";
        case "saving": return "Adding it to the meeting…";
        case "done": return "Recording saved to Drive";
        default: return "Recording not saved";
    }
}

function JobCard({ job }: { job: RecordingSaveJob }) {
    const inProgress = job.phase === "uploading" || job.phase === "sharing" || job.phase === "saving";
    const failed = job.phase === "error";
    return (
        <div
            role={failed ? "alert" : "status"}
            className={cn(
                "pointer-events-auto overflow-hidden rounded-lg border bg-card text-card-foreground shadow-lg",
                failed && "border-destructive/60"
            )}
        >
            <div className="flex items-start gap-2.5 p-3">
                {inProgress ? (
                    <Loader2 className="mt-0.5 h-4 w-4 shrink-0 animate-spin text-primary" aria-hidden />
                ) : failed || job.error ? (
                    <AlertTriangle className={cn("mt-0.5 h-4 w-4 shrink-0", failed ? "text-destructive" : "text-amber-500")} aria-hidden />
                ) : (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
                )}
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{title(job)}</p>
                    <p className="truncate text-[11px] text-muted-foreground" title={job.fileName}>
                        {job.fileName} · {formatBytes(job.sizeBytes)}
                    </p>
                    {job.error ? (
                        <p className={cn("mt-1 text-[11px]", failed ? "text-destructive" : "text-amber-700 dark:text-amber-300")}>{job.error}</p>
                    ) : null}
                    {inProgress && job.phase === "uploading" ? (
                        <p className="mt-1 text-[11px] text-muted-foreground">Keep this tab open until it finishes.</p>
                    ) : null}
                    {!inProgress ? (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {failed ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => recordingSaver.retry(job.id)}
                                        className="flex h-7 items-center gap-1 rounded-md bg-primary px-2 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                                    >
                                        <RotateCw className="h-3.5 w-3.5" /> Retry
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => recordingSaver.downloadCopy(job.id)}
                                        className="flex h-7 items-center gap-1 rounded-md border px-2 text-xs hover:bg-accent"
                                    >
                                        <Download className="h-3.5 w-3.5" /> Download a copy
                                    </button>
                                </>
                            ) : (
                                <a
                                    href={recordingSaver.driveUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex h-7 items-center gap-1 rounded-md border px-2 text-xs hover:bg-accent"
                                >
                                    <FolderOpen className="h-3.5 w-3.5" /> Open Drive
                                </a>
                            )}
                        </div>
                    ) : null}
                </div>
                {!inProgress ? (
                    <button
                        type="button"
                        onClick={() => recordingSaver.dismiss(job.id)}
                        aria-label={failed ? "Discard this recording" : "Dismiss"}
                        title={failed ? "Discard this recording" : "Dismiss"}
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded hover:bg-accent"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                ) : null}
            </div>
            {job.phase === "uploading" ? (
                <div
                    className="h-1 bg-muted"
                    role="progressbar"
                    aria-label="Upload progress"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(job.progress * 100)}
                >
                    <div className="h-full bg-primary transition-[width]" style={{ width: `${Math.max(2, job.progress * 100)}%` }} />
                </div>
            ) : null}
        </div>
    );
}
